import React, { useState, useEffect } from 'react';
import { Copy, Check, Twitter, Send, Zap, Users, BarChart3, Shield } from 'lucide-react';
import NadFunBuyButton from '../web3/NadFunBuyButton';
import { CA_ADDRESS, SOCIAL_LINKS, TOKEN_INFO } from '../../config/constants';

const Hero: React.FC = () => {
    const [copied, setCopied] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    const handleCopy = () => {
        navigator.clipboard.writeText(CA_ADDRESS);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            setMousePos({ x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight });
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    return (
        <section id="about" className="hero">
            {/* Animated Background */}
            <div className="hero-bg" />
            <div className="hero-glow-1" style={{
                transform: `translate(${mousePos.x * 30}px, ${mousePos.y * 30}px)`,
            }} />
            <div className="hero-glow-2" style={{
                transform: `translate(${-mousePos.x * 20}px, ${-mousePos.y * 20}px)`,
            }} />
            <div className="hero-grid-overlay" />

            <div className="hero-content">
                {/* Two Column Layout */}
                <div className="hero-layout">
                    {/* Left: Text Content */}
                    <div className="hero-text-col">
                        {/* Live Badge */}
                        <div className="hero-badge">
                            <span className="hero-badge-dot" />
                            <span>Live on Monad Mainnet</span>
                        </div>

                        {/* Title */}
                        <h1 className="hero-title">
                            <span className="hero-title-duck">DUCK</span>
                            <span className="hero-title-sub">on Monad</span>
                        </h1>

                        {/* Subtitle */}
                        <p className="hero-subtitle">
                            AI-powered memecoin with <span className="hero-highlight">7 autonomous agents</span> delivering
                            real-time trading signals, whale tracking & market intelligence
                            on the fastest EVM chain.
                        </p>

                        {/* Stats Row */}
                        <div className="hero-stats">
                            <div className="hero-stat">
                                <Zap size={16} color="#FFD700" />
                                <div>
                                    <span className="hero-stat-value">7</span>
                                    <span className="hero-stat-label">AI Agents</span>
                                </div>
                            </div>
                            <div className="hero-stat">
                                <BarChart3 size={16} color="#22c55e" />
                                <div>
                                    <span className="hero-stat-value">10K</span>
                                    <span className="hero-stat-label">TPS</span>
                                </div>
                            </div>
                            <div className="hero-stat">
                                <Shield size={16} color="#836EF9" />
                                <div>
                                    <span className="hero-stat-value">400ms</span>
                                    <span className="hero-stat-label">Blocks</span>
                                </div>
                            </div>
                            <div className="hero-stat">
                                <Users size={16} color="#FF8C00" />
                                <div>
                                    <span className="hero-stat-value">24/7</span>
                                    <span className="hero-stat-label">Monitoring</span>
                                </div>
                            </div>
                        </div>

                        {/* CTA Buttons */}
                        <div className="hero-buttons">
                            <NadFunBuyButton size="lg" />
                            <div className="hero-social-buttons">
                                <a
                                    href={SOCIAL_LINKS.twitter}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="hero-btn-social"
                                    aria-label="Follow on X"
                                >
                                    <Twitter size={20} />
                                </a>
                                <a
                                    href={SOCIAL_LINKS.telegram}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="hero-btn-social"
                                    aria-label="Join Telegram"
                                >
                                    <Send size={20} />
                                </a>
                            </div>
                        </div>

                        {/* Contract Address */}
                        <div className="contract-box">
                            <span className="contract-label">CA</span>
                            <span className="contract-address">{CA_ADDRESS}</span>
                            <button
                                onClick={handleCopy}
                                className={`contract-copy-btn ${copied ? 'copied' : ''}`}
                                aria-label="Copy contract address"
                            >
                                {copied ? <Check size={16} /> : <Copy size={16} />}
                            </button>
                        </div>
                    </div>

                    {/* Right: Duck Mascot */}
                    <div className="hero-visual-col">
                        <div className="hero-duck-container">
                            {/* Orbiting rings */}
                            <div className="hero-orbit hero-orbit-1" />
                            <div className="hero-orbit hero-orbit-2" />

                            <div className="hero-duck-frame">
                                <img
                                    src="/images/duck.png"
                                    alt="DUCK Logo"
                                    className="hero-duck-image"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                />
                            </div>
                            <div className="hero-quack">{TOKEN_INFO.tagline}</div>

                            {/* Floating feature tags */}
                            <div className="hero-float-tag hero-float-tag-1">
                                <Zap size={12} /> Trading Oracle
                            </div>
                            <div className="hero-float-tag hero-float-tag-2">
                                <BarChart3 size={12} /> Whale Tracker
                            </div>
                            <div className="hero-float-tag hero-float-tag-3">
                                <Shield size={12} /> MEV Protection
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
